import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Stack, MenuItem, Select, FormControl, InputLabel,
  Divider, Typography, Grid, CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import salesAPI from '../../api/sales';
import { STATUS_CONFIG, TIMEZONE_LABELS } from './SalesDashboard';

const BLANK = {
  status: 'new_lead', priority: 'medium',
  timezone: 'America/New_York', source: 'manual',
  businessName: '', businessEntityType: '', businessEIN: '',
  businessAddress: '', businessCity: '', businessState: '', businessZip: '',
  businessPhone: '', businessWebsite: '', industry: '',
  monthlyRevenue: '', requestedAmount: '', fundingPurpose: '',
  firstName: '', lastName: '', email: '', phone: '', mobilePhone: '',
  title: '', ownershipPct: '',
  owner2FirstName: '', owner2LastName: '', owner2Email: '', owner2Phone: '', owner2Title: '',
  nextFollowUpDate: '', tags: '',
};

export default function LeadFormDialog({ open, lead, onClose, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (lead) {
        setForm({
          ...BLANK, ...lead,
          monthlyRevenue: lead.monthlyRevenue ?? '',
          requestedAmount: lead.requestedAmount ?? '',
          ownershipPct: lead.ownershipPct ?? '',
          tags: Array.isArray(lead.tags) ? lead.tags.join(', ') : '',
          nextFollowUpDate: lead.nextFollowUpDate ? lead.nextFollowUpDate.split('T')[0] : '',
        });
      } else {
        setForm(BLANK);
      }
    }
  }, [open, lead]);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.businessName && !form.firstName && !form.email) {
      toast.warning('Please enter at least a business name, owner name, or email');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        monthlyRevenue: form.monthlyRevenue ? parseFloat(form.monthlyRevenue) : undefined,
        requestedAmount: form.requestedAmount ? parseFloat(form.requestedAmount) : undefined,
        ownershipPct: form.ownershipPct ? parseFloat(form.ownershipPct) : undefined,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        nextFollowUpDate: form.nextFollowUpDate || undefined,
      };

      if (lead?._id) {
        await salesAPI.updateLead(lead._id, payload);
        toast.success('Lead updated');
      } else {
        await salesAPI.createLead(payload);
        toast.success('Lead created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle fontWeight={700}>{lead ? 'Edit Lead' : 'New Lead'}</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Stack gap={2}>

          {/* Status & Priority row */}
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 165 }}>
              <InputLabel>Status</InputLabel>
              <Select value={form.status} label="Status" onChange={set('status')}>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Priority</InputLabel>
              <Select value={form.priority} label="Priority" onChange={set('priority')}>
                {['urgent','high','medium','low'].map((p) => <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 175 }}>
              <InputLabel>Timezone</InputLabel>
              <Select value={form.timezone} label="Timezone" onChange={set('timezone')}>
                {Object.entries(TIMEZONE_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Source</InputLabel>
              <Select value={form.source} label="Source" onChange={set('source')}>
                {['manual','referral','website','cold_call','email_campaign','csv_import','other'].map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Divider><Typography variant="caption">Business Information</Typography></Divider>
          <Grid container spacing={1.5}>
            {[
              ['businessName', 'Business Name', 6],
              ['businessEntityType', 'Entity Type', 3],
              ['businessEIN', 'EIN', 3],
              ['industry', 'Industry', 4],
              ['businessPhone', 'Business Phone', 4],
              ['businessWebsite', 'Website', 4],
              ['businessAddress', 'Address', 6],
              ['businessCity', 'City', 3],
              ['businessState', 'State', 1.5],
              ['businessZip', 'ZIP', 1.5],
              ['monthlyRevenue', 'Monthly Revenue ($)', 4],
              ['requestedAmount', 'Requested Amount ($)', 4],
              ['fundingPurpose', 'Funding Purpose', 4],
            ].map(([field, label, xs]) => (
              <Grid item xs={12} sm={xs} key={field}>
                <TextField label={label} size="small" fullWidth value={form[field]} onChange={set(field)} type={field.toLowerCase().includes('revenue') || field.toLowerCase().includes('amount') ? 'number' : 'text'} />
              </Grid>
            ))}
          </Grid>

          <Divider><Typography variant="caption">Owner 1</Typography></Divider>
          <Grid container spacing={1.5}>
            {[
              ['firstName', 'First Name', 4],
              ['lastName', 'Last Name', 4],
              ['title', 'Title', 4],
              ['email', 'Email', 4],
              ['phone', 'Phone', 4],
              ['mobilePhone', 'Mobile Phone', 4],
              ['ownershipPct', 'Ownership %', 4],
            ].map(([field, label, xs]) => (
              <Grid item xs={12} sm={xs} key={field}>
                <TextField label={label} size="small" fullWidth value={form[field]} onChange={set(field)} type={field === 'ownershipPct' ? 'number' : 'text'} />
              </Grid>
            ))}
          </Grid>

          <Divider><Typography variant="caption">Owner 2 (Optional)</Typography></Divider>
          <Grid container spacing={1.5}>
            {[
              ['owner2FirstName', 'First Name', 4],
              ['owner2LastName', 'Last Name', 4],
              ['owner2Title', 'Title', 4],
              ['owner2Email', 'Email', 4],
              ['owner2Phone', 'Phone', 4],
            ].map(([field, label, xs]) => (
              <Grid item xs={12} sm={xs} key={field}>
                <TextField label={label} size="small" fullWidth value={form[field]} onChange={set(field)} />
              </Grid>
            ))}
          </Grid>

          <Divider><Typography variant="caption">Follow-up & Tags</Typography></Divider>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={5}>
              <TextField label="Follow-up Date" size="small" fullWidth type="date" value={form.nextFollowUpDate} onChange={set('nextFollowUpDate')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField label="Tags (comma-separated)" size="small" fullWidth value={form.tags} onChange={set('tags')} placeholder="hot lead, sba, repeat" />
            </Grid>
          </Grid>

        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : lead ? 'Save Changes' : 'Create Lead'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
